// FILE: /src/Components/Public/PageSelectionView.js (NEW FILE)

import React, { useState } from 'react';
import { Button, Checkbox, List, Avatar } from 'antd';

const PageSelectionView = ({
  accounts,
  platformName,
  onFinalize,
  onCancel,
  accountNameKey = 'name',
  accountImageKey = 'profile_picture_url'
}) => {
  const [selectedItems, setSelectedItems] = useState([]);
  const [imageErrors, setImageErrors] = useState({});

  const handleItemSelect = (item, isChecked) => {
    const itemId = item.id || item.urn || item.platform_id;
    setSelectedItems(prev =>
      isChecked
        ? [...prev, item]
        : prev.filter(i => (i.id || i.urn || i.platform_id) !== itemId)
    );
  };

  const getItemId = (account) => {
    return account.id || account.urn || account.platform_id || account.user_id;
  };

  const getName = (account) => {
    return (
      account?.[accountNameKey] ||
      account?.name ||
      account?.username ||
      account?.profile ||
      'Unnamed Account'
    );
  };

  const getImage = (account) => {
    if (typeof accountImageKey === 'function') {
      return accountImageKey(account);
    }

    return (
      account?.[accountImageKey] ||
      account?.profile_picture_blob_url ||
      account?.profile_picture_url ||
      account?.profile_picture_source_url ||
      account?.profile_image ||
      account?.picture?.data?.url ||
      account?.logo ||
      null
    );
  };

  const getInitials = (name = '') => {
    if (!name) return '?';
    const words = name.trim().split(' ').filter(Boolean);
    if (words.length > 1) {
      return `${words[0][0]}${words[words.length - 1][0]}`.toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  };

  const isSelected = (account) => {
    const itemId = getItemId(account);
    return selectedItems.some(i => getItemId(i) === itemId);
  };

  const renderAvatar = (account) => {
    const itemId = getItemId(account);
    const imageUrl = getImage(account);
    const name = getName(account);

    if (!imageUrl || imageErrors[itemId]) {
      return (
        <div className="w-10 h-10 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-xs font-bold">
          {getInitials(name)}
        </div>
      );
    }

    return (
      <Avatar
        src={imageUrl}
        size={40}
        onError={() => {
          setImageErrors(prev => ({ ...prev, [itemId]: true }));
          return false;
        }}
      />
    );
  };

  return (
    <div className="flex justify-center items-center min-h-screen bg-gray-50 p-4">
      <div className="w-full max-w-lg bg-white p-6 rounded-lg shadow-lg">
        <h3 className="text-xl font-semibold mb-2">Select {platformName} Accounts</h3>
        <p className="text-gray-500 mb-4">Choose which accounts you'd like to connect.</p>

        <div className="h-72 overflow-y-auto border rounded-lg p-2">
          <List
            dataSource={accounts}
            renderItem={acc => {
              const itemId = getItemId(acc);
              return (
                <List.Item
                  key={itemId}
                  actions={[
                    <Checkbox
                      onChange={(e) => handleItemSelect(acc, e.target.checked)}
                      checked={isSelected(acc)}
                    />
                  ]}
                >
                  <List.Item.Meta
                    avatar={renderAvatar(acc)}
                    title={
                      <span className="font-semibold">
                        {getName(acc)}
                      </span>
                    }
                  />
                </List.Item>
              );
            }}
            locale={{ emptyText: "No accounts found." }}
          />
        </div>

        <div className="flex justify-end gap-2 mt-4">
          <Button key="back" onClick={onCancel}>Cancel</Button>
          <Button
            key="submit"
            type="primary"
            onClick={() => onFinalize(selectedItems)}
            disabled={selectedItems.length === 0}
          >
            Connect ({selectedItems.length})
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PageSelectionView;